import React from "react";

interface ListItem {
  title: React.ReactNode;
  description: React.ReactNode;
}

interface ParagraphContent {
  type: "paragraph";
  text: React.ReactNode;
}

interface HeadingContent {
  type: "heading";
  text: React.ReactNode;
}

interface NumberedHeadingContent {
  type: "numberedHeading";
  text: React.ReactNode;
}

interface SubParagraphContent {
  type: "subParagraph";
  text: React.ReactNode;
}

interface ListContent {
  type: "list";
  items: ListItem[];
}

type ContentTerms =
  | ParagraphContent
  | HeadingContent
  | NumberedHeadingContent
  | SubParagraphContent
  | ListContent;

const parseTermsRulesNotice = (text: string): ContentTerms[] => {
  const lines = text.split("\n");
  const structuredContent: ContentTerms[] = [];
  let currentList: ListItem[] | null = null;

  const processText = (input: string): React.ReactNode => {
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
    const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

    let result: React.ReactNode[] = [input];

    // Обробка посилань [текст](URL)
    result = result.flatMap((part) => {
      if (typeof part !== "string") return part;
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;

      while ((match = linkRegex.exec(part)) !== null) {
        const [fullMatch, linkText, url] = match;
        const startIndex = match.index;

        if (startIndex > lastIndex) {
          parts.push(part.slice(lastIndex, startIndex));
        }
        parts.push(
          <a key={`${url}-${startIndex}`} href={url}>
            {linkText}
          </a>
        );
        lastIndex = startIndex + fullMatch.length;
      }

      if (lastIndex < part.length) {
        parts.push(part.slice(lastIndex));
      }
      return parts.length ? parts : part;
    });

    // Обробка email-адрес
    result = result.flatMap((part) => {
      if (typeof part !== "string") return part;
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;

      while ((match = emailRegex.exec(part)) !== null) {
        const email = match[0];
        const startIndex = match.index;

        if (startIndex > lastIndex) {
          parts.push(part.slice(lastIndex, startIndex));
        }
        parts.push(
          <a key={`${email}-${startIndex}`} href={`mailto:${email}`}>
            {email}
          </a>
        );
        lastIndex = startIndex + email.length;
      }

      if (lastIndex < part.length) {
        parts.push(part.slice(lastIndex));
      }
      return parts.length ? parts : part;
    });

    return result.length === 1 ? result[0] : result;
  };

  lines.forEach((line) => {
    line = line.trim();
    if (!line) return;

    // Обробка списків (починаються з ●)
    if (line.startsWith("●")) {
      if (!currentList) currentList = [];
      const listItem = line.slice(1).trim(); // Видаляємо ●
      const [title, ...description] = listItem.split(": ");
      currentList.push({
        title: processText(title || ""),
        description: processText(
          description.length ? description.join(": ") : ""
        ),
      });
      return;
    }

    // Завершуємо список, якщо він є
    if (currentList) {
      structuredContent.push({ type: "list", items: currentList });
      currentList = null;
    }

    // Хедінги без цифр (починаються з ||)
    if (line.startsWith("||")) {
      const headingText = line.slice(2).trim();
      structuredContent.push({
        type: "heading",
        text: `${processText(headingText)}`,
      });
      return;
    }

    // Нумеровані хедінги верхнього рівня (починаються з "X. ")
    if (/^\d+\.\s/.test(line)) {
      structuredContent.push({
        type: "numberedHeading",
        text: processText(line),
      });
      return;
    }

    // Підпараграфи (починаються з "X.Y" або "X.Y.Z")
    if (/^\d+\.\d+(\.\d+)?\s/.test(line)) {
      structuredContent.push({
        type: "subParagraph",
        text: processText(line),
      });
      return;
    }

    // Усі інші рядки — параграфи
    structuredContent.push({ type: "paragraph", text: processText(line) });
  });

  if (currentList) {
    structuredContent.push({ type: "list", items: currentList });
  }

  return structuredContent;
};

export default parseTermsRulesNotice;
